import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit3, Mail, Phone, Lock, User, Briefcase, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { staffService } from '../../services/staffService';
import { customToast } from '../../utils/customToast';
import { StaffMember, StaffRoleType, UpdateStaffRequest } from '../../types';

interface EditStaffModalProps {
    isOpen: boolean;
    staff: StaffMember | null;
    onClose: () => void;
    onSuccess: () => void;
}

const EditStaffModal: React.FC<EditStaffModalProps> = ({ isOpen, staff, onClose, onSuccess }) => {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [formData, setFormData] = useState<UpdateStaffRequest>({
        username: '',
        email: '',
        password: '',
        full_name: '',
        phone: '',
        staff_role_type: 'seller',
        is_active: true,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen && staff) {
            setFormData({
                username: staff.username,
                email: staff.email,
                password: '',
                full_name: staff.full_name,
                phone: staff.phone || '',
                staff_role_type: staff.staff_role_type,
                is_active: staff.is_active,
            });
        }
    }, [isOpen, staff]);

    if (!isOpen || !staff) return null;

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.username?.trim()) newErrors.username = 'Username is required';
        if (!formData.email?.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (formData.password && formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (!formData.full_name?.trim()) newErrors.full_name = 'Full name is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            // Only send password if it was changed
            const updateData: UpdateStaffRequest = {
                username: formData.username,
                email: formData.email,
                full_name: formData.full_name,
                phone: formData.phone,
                staff_role_type: formData.staff_role_type,
                is_active: formData.is_active,
            };

            if (formData.password) {
                updateData.password = formData.password;
            }

            await staffService.updateStaff(staff.id, updateData);
            customToast.success('Staff member updated successfully');
            onSuccess();
            handleClose();
        } catch (error: any) {
            console.error('Error updating staff:', error);
            const message = error.response?.data?.message || 'Failed to update staff member';
            customToast.error(message);

            if (error.response?.data?.errors) {
                const backendErrors: Record<string, string> = {};
                Object.entries(error.response.data.errors).forEach(([key, msgs]: [string, any]) => {
                    backendErrors[key] = Array.isArray(msgs) ? msgs[0] : msgs;
                });
                setErrors(backendErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setFormData({
                username: '',
                email: '',
                password: '',
                full_name: '',
                phone: '',
                staff_role_type: 'seller',
                is_active: true,
            });
            setErrors({});
            onClose();
        }, 150);
    };

    const roleOptions: { value: StaffRoleType; label: string }[] = [
        { value: 'manager', label: 'Manager' },
        { value: 'seller', label: 'Seller' },
        { value: 'worker', label: 'Worker' },
    ];

    return createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}>
            <div className="flex items-center justify-center min-h-screen p-4">
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                    onClick={handleClose}
                />

                <div className={`relative w-full max-w-2xl overflow-hidden text-left transform shadow-2xl rounded-2xl ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
                    } ${theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}`}>
                    {/* Header */}
                    <div className={`px-4 sm:px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'
                                    }`}>
                                    <Edit3 className={`w-5 h-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
                                        }`} />
                                </div>
                                <div>
                                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        Edit Staff Member
                                    </h2>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Update account information
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className={`p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'
                                    }`}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="px-4 sm:px-6 py-4 max-h-[calc(100vh-16rem)] sm:max-h-[60vh] overflow-y-auto">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Username */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Username *
                                    </label>
                                    <div className="relative">
                                        <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                        <input
                                            type="text"
                                            value={formData.username || ''}
                                            onChange={(e) => handleInputChange('username', e.target.value)}
                                            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.username ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                        />
                                    </div>
                                    {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Email *
                                    </label>
                                    <div className="relative">
                                        <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                        <input
                                            type="email"
                                            value={formData.email || ''}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.email ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                        />
                                    </div>
                                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                </div>

                                {/* Full Name */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.full_name || ''}
                                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                                        className={`w-full px-4 py-2 rounded-lg border ${errors.full_name ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                            } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                    />
                                    {errors.full_name && <p className="mt-1 text-sm text-red-500">{errors.full_name}</p>}
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Phone
                                    </label>
                                    <div className="relative">
                                        <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                        <input
                                            type="tel"
                                            value={formData.phone || ''}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        New Password (leave blank to keep current)
                                    </label>
                                    <div className="relative">
                                        <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password || ''}
                                            onChange={(e) => handleInputChange('password', e.target.value)}
                                            className={`w-full pl-10 pr-10 py-2 rounded-lg border ${errors.password ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                                </div>

                                {/* Role Type */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Role Type
                                    </label>
                                    <div className="relative">
                                        <Briefcase className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                        <select
                                            value={formData.staff_role_type || 'seller'}
                                            onChange={(e) => handleInputChange('staff_role_type', e.target.value)}
                                            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                        >
                                            {roleOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Active Status */}
                                <div className={`flex items-center justify-between p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                    <div>
                                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                                            Account Active
                                        </p>
                                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Disabled accounts cannot log in
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange('is_active', !formData.is_active)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-gray-400'
                                            }`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-1'
                                            }`} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={`px-4 sm:px-6 py-4 border-t flex flex-col-reverse sm:flex-row space-y-reverse space-y-2 sm:space-y-0 sm:space-x-3 ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                            }`}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className={`w-full sm:flex-1 px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg border transition-colors ${theme === 'dark'
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:flex-1 px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : null}
                                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EditStaffModal;
